import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Galleria } from 'primereact/galleria'
import { Column } from 'primereact/column'
import { Avatar } from 'primereact/avatar'
import { TABLE_CONST } from 'Utils/Constants'
import { ButtonComponent, DialogModal } from 'Components/UIComponents'
import Loader from 'Components/Loader'
import LayoutContainer from 'Components/LayoutComponent'
import DialogUIModal from 'Components/UIComponents/DialogUIModal'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'

const PostApproval = ({ setLoading }) => {
  const { t } = useTranslation()
  let rowCount = 8
  let paginationButtons = TABLE_CONST.paginationButtonCount

  const [windowSize, setWindowSize] = useState(window.innerWidth)
  const [unapprovedPosts, setUnapprovedPosts] = useState([])
  const [selectedMessage, setSelectedMessage] = useState(null)
  const [openDialogModal, setOpenDialogModal] = useState(false)
  const [expandedDescriptions, setExpandedDescriptions] = useState({})
  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {} })

  if (windowSize < TABLE_CONST.maxWidthTab) {
    rowCount = TABLE_CONST.mobileRowCount
    paginationButtons = TABLE_CONST.mobilePaginationButtonCount
  }

  useEffect(() => {
    fetchGetAllUnapprovedMediaPost()

    const handleWindowResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const fetchGetAllUnapprovedMediaPost = async () => {
    setLoading(true)
    try {
      const response = await apiAdapterCoffeeWeb.getCoffeeWebSocialPostApproval()

      setUnapprovedPosts(response?.data)
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG'),
        onRetry: () => postApprovalAndRejectionForPost(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const postApprovalAndRejectionForPost = async (postId, isVerified) => {
    setLoading(true)

    const payload = {
      postId,
      isVerified
    }

    try {
      const response = await apiAdapterCoffeeWeb.postApprovalPostForCoffeeWebSocial(payload)

      if (response.status === 200) {
        setOpenDialogModal(false)
        fetchGetAllUnapprovedMediaPost()

        setShowDialog((prev) => ({
          ...prev,
          showModal: true,
          modalType: 'success',
          message: t('POST_HAS_BEEN_SUCCESSFULLY_APPROVED'),
          onSuccess: handleDismiss,
          onHide: handleDismiss
        }))
      }
    } catch (error) {
      setShowDialog((prev) => ({
        ...prev,
        showModal: true,
        modalType: 'error',
        message: t('SOMETHING_WENT_WRONG'),
        onRetry: () => postApprovalAndRejectionForPost(postId, isVerified),
        onHide: handleDismiss
      }))
    } finally {
      setLoading(false)
    }
  }

  const handleRowClick = (rowData) => {
    setSelectedMessage(rowData)
    setOpenDialogModal(true)
  }

  const handleDismiss = () => {
    setShowDialog({ ...showDialog, showModal: false })
  }

  const handleCloseModal = () => {
    setOpenDialogModal(false)
  }

  const customActionButton = (rowData) => (
    <div className="post-approval-action-button-wrapper">
      <Button type="button" icon="pi pi-cog" className="action-add-button" tooltip={t('APPROVE_URL')} tooltipOptions={{ position: 'top' }} onClick={() => handleRowClick(rowData)} />
    </div>
  )

  const renderChannelLogo = (rowData) => <Avatar image={rowData?.channelLogo} shape="circle" size="large" alt={rowData?.channelName} />

  const renderChannelLink = (rowData) => {
    const link = rowData.channelLink

    if (!link) return '--'

    return (
      <a href={link} target="_blank" rel="noopener noreferrer" className="channel-link-ellipsis">
        {link}
      </a>
    )
  }

  const columns = [
    {
      dataField: 'channelLogo',
      text: t('CHANNEL_LOGO'),
      className: 'channel-logo-column',
      sortable: false,
      body: renderChannelLogo
    },

    {
      dataField: 'channelName',
      text: t('CHANNEL_NAME'),
      className: 'channel-name-column',
      sortable: false
    },
    {
      dataField: 'country',
      text: t('COUNTRY_ID'),
      className: 'country-id-column',
      sortable: false
    },
    {
      dataField: 'channelLink',
      text: t('CHANNEL_LINK'),
      className: 'channel-link-column',
      sortable: false,
      body: renderChannelLink
    },

    {
      text: t('ACTIONS'),
      className: 'column-actions',
      body: customActionButton
    }
  ]

  const renderFooter = () => (
    <div className="approval-modal-footer-btns">
      {/* eslint-disable-next-line no-underscore-dangle */}
      <ButtonComponent type={'Custom'} label={'REJECT'} variant="neutral" icon="pi pi-times-circle" onClick={() => postApprovalAndRejectionForPost(selectedMessage?._id, false)} disabled />
      {/* eslint-disable-next-line no-underscore-dangle */}
      <ButtonComponent type={'Custom'} label={'APPROVE'} variant="action" icon="pi pi-check-circle" onClick={() => postApprovalAndRejectionForPost(selectedMessage?._id, true)} />
    </div>
  )

  const toggleDescription = (postId) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [postId]: !prev[postId]
    }))
  }

  const truncateDescription = (text, wordLimit = 20) => {
    if (!text) return ''
    const words = text.split(' ')

    return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') : text
  }

  const renderDescription = (post) => {
    const isExpanded = expandedDescriptions[post.id]
    const truncatedText = truncateDescription(post.description, 20)
    const fullText = post.description
    const shouldShowButton = post.description && post.description.split(' ').length > 20

    return (
      <>
        {isExpanded ? fullText : truncatedText} {!isExpanded && shouldShowButton && '...'} {shouldShowButton && <Button label={isExpanded ? t('READ_LESS') : t('READ_MORE')} className="p-button-link" onClick={() => toggleDescription(post.id)} style={{ padding: '0', marginTop: '8px' }} />}
      </>
    )
  }

  const isVideo = (type) => type?.includes('video')

  const buildGalleriaItems = (media = []) =>
    media.map(({ mediaurl, medianame, mediatype }) => ({
      src: mediaurl,
      alt: medianame,
      title: medianame,
      type: mediatype
    }))

  const ItemTemplate = ({ src, alt, type }) =>
    isVideo(type) ? (
      <video width="100%" controls>
        <source src={src} type={type} />
        Your browser does not support the video tag.
      </video>
    ) : (
      <img src={src} alt={alt} width="100%" />
    )

  const renderSingleMedia = (item) => (
    <div>
      <p>
        <b>{t('MEDIA')}:</b>
      </p>
      <ItemTemplate {...item} />
    </div>
  )

  const renderMediaSection = (items = []) => {
    if (!items.length) return null

    if (items.length === 1) {
      return renderSingleMedia(items[0])
    }

    return (
      <div>
        <p>
          <b>{t('MEDIA')}:</b>
        </p>

        <Galleria value={items} item={(item) => <ItemTemplate {...item} />} showItemNavigators showItemNavigatorsOnHover showIndicators showIndicatorsOnItem indicatorsPosition="bottom" showThumbnails={false} style={{ maxWidth: '100%' }} />
      </div>
    )
  }

  const renderBodyData = () => {
    if (!selectedMessage) return null

    const mediaItems = buildGalleriaItems(selectedMessage.media)

    return (
      <div className="modal-card-wrapper">
        <div className="modal-card-item">
          <p>
            <b>{t('CHANNEL_NAME')}:</b> {selectedMessage.channelName}
          </p>
          <p>
            <b>{t('DESCRIPTION')}:</b> {renderDescription(selectedMessage)}
          </p>

          {renderMediaSection(mediaItems)}
        </div>

        <div className="modal-card-item" />
      </div>
    )
  }

  return (
    <LayoutContainer title={t('POST_APPROVAL')} breadCrumbsTitle={'postApproval'}>
      <div className="post-approval-list-table">
        <DataTable value={unapprovedPosts} paginator={unapprovedPosts?.length} rows={rowCount} pageLinkSize={paginationButtons}>
          {columns?.map((column, index) => (
            <Column key={index} field={column.dataField} header={column.text} body={column.body} className={column.className} sortable={column.sortable} />
          ))}
        </DataTable>
      </div>
      <DialogModal {...showDialog} onDismiss={handleDismiss} />

      <DialogUIModal visible={openDialogModal} header={'Post Approval'} onHide={handleCloseModal} body={renderBodyData} footer={renderFooter} className="post-approval-modal" size="md" maximizable={false} />
    </LayoutContainer>
  )
}

export default Loader(PostApproval)
